/**
 * Documents Management Page
 * Centralized document storage for properties, contracts, and sales documents
 */

import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, Search, Filter, Folder, File, Image, FileSpreadsheet,
  Download, Trash2, Eye, Upload, MoreHorizontal, Grid, List,
  FolderOpen, Clock, User, Building
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fileTypeIcons = {
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  jpg: Image,
  jpeg: Image,
  png: Image,
  default: File
};

const categoryColors = {
  contract: 'bg-blue-500/10 text-blue-500',
  property: 'bg-green-500/10 text-green-500',
  legal: 'bg-purple-500/10 text-purple-500',
  financial: 'bg-yellow-500/10 text-yellow-500',
  marketing: 'bg-pink-500/10 text-pink-500',
  other: 'bg-gray-500/10 text-gray-500'
};

const DocumentCard = ({ document, viewMode, onView, onDownload, onDelete }) => {
  const ext = document.filename?.split('.').pop()?.toLowerCase() || 'default';
  const FileIcon = fileTypeIcons[ext] || fileTypeIcons.default;
  const categoryStyle = categoryColors[document.category] || categoryColors.other;

  if (viewMode === 'list') {
    return (
      <div 
        className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors border-b cursor-pointer"
        onClick={() => onView(document)}
        data-testid={`doc-${document.id}`}
      >
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
          <FileIcon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{document.name}</p>
          <p className="text-sm text-muted-foreground">{document.filename}</p>
        </div>
        <Badge className={categoryStyle}>{document.category}</Badge>
        <span className="text-sm text-muted-foreground">{document.size}</span>
        <span className="text-sm text-muted-foreground">
          {new Date(document.uploaded_at).toLocaleDateString()}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onView(document); }}>
              <Eye className="h-4 w-4 mr-2" /> View
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDownload(document); }}>
              <Download className="h-4 w-4 mr-2" /> Download
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-red-600"
              onClick={(e) => { e.stopPropagation(); onDelete(document); }}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <Card 
      className="hover:shadow-lg transition-all cursor-pointer group"
      onClick={() => onView(document)}
      data-testid={`doc-${document.id}`}
    >
      <CardContent className="p-4">
        <div className="aspect-square rounded-lg bg-muted flex items-center justify-center mb-3 relative">
          <FileIcon className="h-12 w-12 text-muted-foreground" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
            <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); onView(document); }}>
              <Eye className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); onDownload(document); }}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div>
          <p className="font-medium truncate text-sm">{document.name}</p>
          <div className="flex items-center justify-between mt-2">
            <Badge className={`${categoryStyle} text-xs`}>{document.category}</Badge>
            <span className="text-xs text-muted-foreground">{document.size}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const FolderCard = ({ folder, onClick }) => (
  <Card 
    className="hover:shadow-lg transition-all cursor-pointer"
    onClick={() => onClick(folder)}
    data-testid={`folder-${folder.id}`}
  >
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <Folder className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{folder.name}</p>
          <p className="text-sm text-muted-foreground">{folder.document_count} documents</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Documents() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [currentFolder, setCurrentFolder] = useState(null);

  useEffect(() => {
    fetchDocuments();
    fetchFolders();
    fetchStats();
  }, [categoryFilter, currentFolder]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/documents/stats?token=${token}`);
      const data = await response.json();
      if (data.success) {
        // Update stats display
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchFolders = async () => {
    try {
      const params = new URLSearchParams({ token });
      if (currentFolder) {
        params.append('parent_id', currentFolder.id);
      }
      const response = await fetch(`${API_URL}/api/documents/folders?${params}`);
      const data = await response.json();
      if (data.success) {
        setFolders(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch folders:', err);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ token });
      if (categoryFilter !== 'all') {
        params.append('category', categoryFilter);
      }
      if (currentFolder) {
        params.append('folder_id', currentFolder.id);
      }
      
      const response = await fetch(`${API_URL}/api/documents?${params}`);
      const data = await response.json();
      
      if (data.success && data.data.documents.length > 0) {
        setDocuments(data.data.documents || []);
      } else {
        // Seed demo data if empty
        const seedResponse = await fetch(`${API_URL}/api/documents/seed-demo?token=${token}`, { method: 'POST' });
        const seedData = await seedResponse.json();
        if (seedData.success) {
          // Refetch after seeding
          const retryResponse = await fetch(`${API_URL}/api/documents?${params}`);
          const retryData = await retryResponse.json();
          if (retryData.success) {
            setDocuments(retryData.data.documents || []);
          }
          // Also refetch folders
          fetchFolders();
        }
      }
    } catch (err) {
      toast.error('Failed to load documents');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter(d =>
    !searchQuery ||
    d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.filename?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleView = (document) => {
    toast.info(`Viewing: ${document.name}`);
  };

  const handleDownload = (document) => {
    toast.success(`Downloading: ${document.filename}`);
  };

  const handleDelete = (document) => {
    toast.error(`Delete: ${document.name} (not implemented)`);
  };

  const handleFolderClick = (folder) => {
    setCurrentFolder(folder);
    toast.info(`Opened folder: ${folder.name}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Documents</h1>
          <p className="text-muted-foreground">Manage all your property and sales documents</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="new-folder-btn">
            <FolderOpen className="h-4 w-4 mr-2" />
            New Folder
          </Button>
          <Button data-testid="upload-btn">
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Documents</p>
                <p className="text-2xl font-bold">{documents.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Folder className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Folders</p>
                <p className="text-2xl font-bold">{folders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold">12</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Building className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Storage Used</p>
                <p className="text-2xl font-bold">2.4 GB</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-documents"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="contract">Contracts</SelectItem>
                <SelectItem value="property">Property</SelectItem>
                <SelectItem value="legal">Legal</SelectItem>
                <SelectItem value="financial">Financial</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center border rounded-lg">
              <Button 
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All Documents</TabsTrigger>
          <TabsTrigger value="folders">Folders</TabsTrigger>
          <TabsTrigger value="recent">Recent</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          {filteredDocuments.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">No Documents Found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery || categoryFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Upload your first document to get started'}
                </p>
                <Button>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredDocuments.map(doc => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  viewMode={viewMode}
                  onView={handleView}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                {filteredDocuments.map(doc => (
                  <DocumentCard
                    key={doc.id}
                    document={doc}
                    viewMode={viewMode}
                    onView={handleView}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="folders" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {folders.map(folder => (
              <FolderCard key={folder.id} folder={folder} onClick={handleFolderClick} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="recent" className="mt-6">
          <Card>
            <CardContent className="p-0">
              {documents.slice(0, 5).map(doc => (
                <DocumentCard
                  key={doc.id}
                  document={doc}
                  viewMode="list"
                  onView={handleView}
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
